import { forwardRef, useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
    useFloating,
    useClick,
    useDismiss,
    useRole,
    useInteractions,
    FloatingFocusManager,
    offset,
    flip,
    size,
    autoUpdate,
    useMergeRefs,
    useListNavigation,
} from '@floating-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import PropTypes from 'prop-types';
import OutlineChevronDown from 'Components/Atoms/OutlineIcons/OutlineChevronDown';
import selectVariants from 'Components/Atoms/Select/SelectVariants';
import { cn } from 'Utils/cn';
import ButtonClear from 'Components/Atoms/ButtonClear/ButtonClear';
import useProvidersConnections from 'Hooks/useProvidersConnections';
import SelectProductFilter from './SelectProductFilter';
import { filterProductsByQuery } from './SelectProductUtils';
import { useTranslation } from 'react-i18next';
import ErrorMessage from 'Components/Atoms/ErrorMessage/ErrorMessage';
import FloatLabel from 'Components/Atoms/FloatLabel/FloatLabel';

const SelectProduct = forwardRef(function SelectProduct(props, ref) {
    const { label, size: selectSize, value, onChange, className, disabled, error } = props;
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(null);
    const listRef = useRef([]);

    const { t } = useTranslation('common');

    const { visibleProvidersConnections = [], isLoading } = useProvidersConnections();

    const options = useMemo(() => {
        return visibleProvidersConnections.map((item) => {
            return {
                id: item.id,
                label: item.name,
                icon: <img src={item.logo_url} alt="" className="size-5 object-contain block" />,
                products: item.products.map((product) => ({
                    id: product.id,
                    label: product.name,
                })),
            };
        });
    }, [visibleProvidersConnections]);

    useEffect(() => {
        if (!isLoading && value && options.length > 0) {
            const isValidValue = options.some((group) => group.products.some((product) => product.id === value));
            if (!isValidValue) {
                onChange('');
            }
        }
    }, [isLoading, value, options]);

    const handleFilter = (ev) => {
        setQuery(ev.target.value);
        setActiveIndex(null);
    };

    const optionsFiltered = query
        ? options.map((group) => filterProductsByQuery(group, query)).filter((group) => group.products.length > 0)
        : options;

    const { refs, floatingStyles, context, strategy, x, y } = useFloating({
        open: isOpen,
        onOpenChange: (open) => {
            setIsOpen(open);
            if (!open) {
                setQuery('');
                setActiveIndex(null);
            }
        },
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(4),
            flip(),
            size({
                apply({ rects, elements, availableHeight }) {
                    Object.assign(elements.floating.style, {
                        minWidth: `${rects.reference.width}px`,
                        maxHeight: `${Math.min(availableHeight - 20, 520)}px`,
                    });
                },
            }),
        ],
    });

    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
        useClick(context),
        useRole(context, { role: 'listbox' }),
        useDismiss(context),
        useListNavigation(context, {
            listRef,
            activeIndex,
            onNavigate: setActiveIndex,
            virtual: true,
            loop: true,
            focusItemOnOpen: true,
        }),
    ]);

    const formattedLabel = useMemo(() => {
        if (!value) return '';

        const group = options.find((g) => g.products.some((p) => p.id === value));
        if (!group) return '';

        const product = group.products.find((p) => p.id === value);
        if (!product) return '';

        return `${group.label} - ${product.label}`;
    }, [value, options]);

    const flatProducts = useMemo(() => optionsFiltered.flatMap((group) => group.products), [optionsFiltered]);

    const handleFloatingKeyDown = useCallback(
        (e) => {
            if ((e.key === 'Enter' || e.key === ' ') && activeIndex != null && flatProducts[activeIndex]) {
                e.preventDefault();
                onChange(flatProducts[activeIndex].id);
                setIsOpen(false);
            }
        },
        [activeIndex, flatProducts],
    );

    const hasError = !!error;
    const buttonRef = useMergeRefs([refs.setReference, ref]);

    const handleClear = () => {
        onChange('');
        setQuery('');
        setIsOpen(false);
    };

    return (
        <div className="relative min-w-0 group">
            <FloatLabel label={label} hasError={hasError} isOpen={isOpen} />

            <button
                ref={buttonRef}
                type="button"
                disabled={disabled}
                className={cn(
                    selectVariants({ size: selectSize, status: hasError ? 'error' : 'default', className }),
                    'gap-1',
                    isOpen && 'border-primary ring-2 ring-primary/20',
                    'text-dark-blue',
                )}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                {...getReferenceProps()}
            >
                <div className="truncate grow min-w-0">{formattedLabel}</div>
                {value && <ButtonClear onClear={handleClear} />}
                <OutlineChevronDown size={20} className="shrink-0 text-dark-blue" color="currentColor" />
            </button>
            <AnimatePresence>
                {isOpen && (
                    <FloatingFocusManager context={context}>
                        <motion.div
                            ref={refs.setFloating}
                            style={{ ...floatingStyles, position: strategy, top: y ?? 0, left: x ?? 0 }}
                            className="mt-1 w-full overflow-x-hidden overflow-y-auto scrollbar-thin rounded-md bg-white shadow-sm ring-1 ring-black/5 focus:outline-none text-sm z-dropdown min-w-[180px]"
                            tabIndex={-1}
                            {...getFloatingProps({ onKeyDown: handleFloatingKeyDown })}
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.15, ease: 'easeOut' }}
                        >
                            <SelectProductFilter value={query} onChange={handleFilter} />

                            <div className="p-1.5">
                                {optionsFiltered.length > 0 ? (
                                    (() => {
                                        let flatIdx = 0;
                                        return optionsFiltered.map((group) => (
                                            <div key={group.id} className="last:pb-2">
                                                <div className="flex items-center gap-2 py-2 px-1 min-w-0 select-none">
                                                    {group.icon && <div className="shrink-0">{group.icon}</div>}
                                                    <div className="text-2xs font-semibold uppercase tracking-widest truncate">
                                                        {group.label}
                                                    </div>
                                                </div>

                                                <div className="border-l border-gray-200 ml-3 pl-2">
                                                    {group.products.map((option) => {
                                                        const idx = flatIdx++;
                                                        return (
                                                            <div
                                                                key={option.id}
                                                                ref={(node) => {
                                                                    listRef.current[idx] = node;
                                                                }}
                                                                tabIndex={activeIndex === idx ? 0 : -1}
                                                                className={cn(
                                                                    'cursor-pointer select-none px-3 py-2 rounded-sm transition-colors outline-none',
                                                                    value === option.id
                                                                        ? 'bg-primary/10 text-primary font-semibold'
                                                                        : activeIndex === idx
                                                                          ? 'bg-gray-100'
                                                                          : 'hover:bg-gray-100',
                                                                )}
                                                                role="option"
                                                                aria-selected={value === option.id}
                                                                {...getItemProps({
                                                                    onClick() {
                                                                        onChange(option.id);
                                                                        setIsOpen(false);
                                                                    },
                                                                })}
                                                            >
                                                                {option.label}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ));
                                    })()
                                ) : (
                                    <div className="text-center text-gray-500 text-xs py-4">
                                        {query ? t('no-results-found') : t('no-products-available')}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </FloatingFocusManager>
                )}
            </AnimatePresence>
            <ErrorMessage message={error} />
        </div>
    );
});

export default SelectProduct;

SelectProduct.propTypes = {
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    enableClear: PropTypes.bool,
    onClear: PropTypes.func,
    size: PropTypes.oneOf(['normal', 'small']),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};
